import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useDomains, RECOMMENDATION_LABEL, type DomainRow, type UnmonitoredDomain } from '@/api/domains';
import { PageHeader } from '@/components/PageHeader';
import { StatCard } from '@/components/dashboard/StatCard';
import { DesktopOnly } from '@/components/dashboard/DesktopOnly';
import { SearchInput } from '@/components/ui/search-input';
import { Journey, Ok, Pct } from '@/components/domains/DomainsKit';
import { fmtRelative } from '@/lib/cobalt-helpers';
import { ROUTES, companyPath, domainPath } from '@/lib/routes';
import { cn } from '@/lib/utils';

/**
 * Domains — every dmarcian-monitored domain on its DMARC enforcement journey, with the client,
 * 7-day compliance and whether it's ready to tighten; plus client mail domains dmarcian doesn't
 * monitor at all (from CIPP's Domain Analyser).
 */

type Show = 'all' | 'ready' | 'none' | 'quarantine' | 'reject' | 'missing' | 'problems' | 'unmapped' | 'unmonitored';
const FILTERS: { id: Show; label: string; test?: (d: DomainRow) => boolean }[] = [
  { id: 'all',         label: 'All',                test: () => true },
  { id: 'ready',       label: 'Ready to tighten',   test: (d) => d.readyToEnforce },
  { id: 'none',        label: 'p=none',             test: (d) => d.stage === 'monitoring' },
  { id: 'quarantine',  label: 'Quarantine',         test: (d) => d.stage === 'quarantine' },
  { id: 'reject',      label: 'Reject',             test: (d) => d.stage === 'reject' },
  { id: 'missing',     label: 'No DMARC',           test: (d) => d.stage === 'missing' },
  { id: 'problems',    label: 'Problems', test: (d) => d.failingSenders > 0 || d.openIssues > 0 || d.spf.ok === false },
  { id: 'unmapped',    label: 'Unmapped',           test: (d) => d.company.id == null },
  { id: 'unmonitored', label: 'Not in dmarcian' },
];

const PREF = 'cosmos:domains:show';

export default function DomainsPage() {
  const { data, isLoading, error, isFetching } = useDomains({ scoped: true });
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [show, setShowState] = useState<Show>(() => { try { return (localStorage.getItem(PREF) as Show) || 'all'; } catch { return 'all'; } });
  const [q, setQ] = useState('');
  const setShow = (s: Show) => { setShowState(s); try { localStorage.setItem(PREF, s); } catch { /* storage unavailable */ } };

  useEffect(() => {
    const s = params.get('show') as Show | null;
    if (!s) return;
    if (FILTERS.some((f) => f.id === s)) setShow(s);
    setParams({}, { replace: true });
  }, [params, setParams]);

  const items = data?.items ?? [];
  const counts = useMemo(() => Object.fromEntries(FILTERS.map((f) => [f.id, f.test ? items.filter(f.test).length : data?.unmonitored.length ?? 0])) as Record<Show, number>, [items, data]);
  const s = q.trim().toLowerCase();
  const rows = useMemo(() => {
    const f = FILTERS.find((x) => x.id === show);
    return f?.test ? items.filter(f.test).filter((d) => !s || d.domain.includes(s) || (d.company.name ?? '').toLowerCase().includes(s)) : [];
  }, [items, show, s]);
  const unmonitored = useMemo(() => (data?.unmonitored ?? []).filter((d) => !s || d.domain.includes(s) || (d.companyName ?? '').toLowerCase().includes(s)), [data, s]);

  return (
    <DesktopOnly title="Domains is available on desktop" message="Open Cosmos on a larger screen to view domains.">
      <div className="mx-auto w-full max-w-[1600px] px-4 py-5 md:px-6">
        <PageHeader title="Domains" count={data ? (show === 'unmonitored' ? unmonitored.length : rows.length) : null} isFetching={isFetching}
          subtitle={data && <>dmarcian (last 7 days of mail) + CIPP DNS checks{data.fetchedAt && <> · read {fmtRelative(data.fetchedAt)}</>}</>}
          actions={<SearchInput withIcon placeholder="Domain or client" className="w-64" value={q} onValueChange={setQ} />} />

        {isLoading ? (
          <div className="flex items-center gap-2 py-16 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : error ? (
          <div className="mt-4 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">{error instanceof Error ? error.message : 'Failed to load'}</div>
        ) : data ? (
          <>
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 min-[1400px]:grid-cols-6">
              <StatCard label="Monitored" value={items.length} sub={`${items.filter((d) => d.active).length} receiving reports`} onClick={() => setShow('all')} />
              <StatCard label="Ready to tighten" value={counts.ready} sub={`≥ ${data.readyPct}% for ${data.readyDays}+ days`} tone={counts.ready ? 'warning' : 'success'} onClick={() => setShow('ready')} />
              <StatCard label="At p=none" value={counts.none} sub="monitoring only" tone={counts.none ? 'warning' : 'success'} onClick={() => setShow('none')} />
              <StatCard label="At reject" value={counts.reject} sub="fully enforced" tone="success" onClick={() => setShow('reject')} />
              <StatCard label="Problems" value={counts.problems} sub="failing senders or DNS" tone={counts.problems ? 'destructive' : 'success'} onClick={() => setShow('problems')} />
              <StatCard label="Not in dmarcian" value={counts.unmonitored} sub="client mail domains" tone={counts.unmonitored ? 'warning' : 'success'} onClick={() => setShow('unmonitored')} />
            </div>

            <div className="mt-4 flex flex-wrap gap-1 rounded-lg bg-muted p-1" style={{ width: 'fit-content' }}>
              {FILTERS.map((f) => (
                <button key={f.id} type="button" onClick={() => setShow(f.id)}
                  className={cn('rounded-md px-3 py-1 text-xs font-medium', show === f.id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')}>
                  {f.label} <span className="ml-1 tabular-nums text-muted-foreground">{counts[f.id]}</span>
                </button>
              ))}
            </div>

            {show === 'unmonitored' ? <UnmonitoredTable rows={unmonitored} /> : (
              <div className="mt-3 overflow-x-auto rounded-lg border border-border bg-card shadow-sm">
                <table className="w-full text-sm" style={{ minWidth: 1100 }}>
                  <thead className="whitespace-nowrap bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 font-medium">Domain</th>
                      <th className="px-3 py-2 font-medium">Client</th>
                      <th className="px-3 py-2 font-medium">DMARC journey</th>
                      <th className="px-3 py-2 text-right font-medium">Mail (7d)</th>
                      <th className="px-3 py-2 font-medium">Compliance</th>
                      <th className="px-3 py-2 font-medium">Next step</th>
                      <th className="px-3 py-2 font-medium">At policy</th>
                      <th className="px-3 py-2 font-medium">SPF</th>
                      <th className="px-3 py-2 font-medium">DKIM</th>
                      <th className="px-3 py-2 font-medium">Senders</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-divider">
                    {rows.length === 0 && <tr><td colSpan={10} className="px-3 py-10 text-center text-muted-foreground">Nothing matches.</td></tr>}
                    {rows.map((d) => (
                      <tr key={d.id} className="cursor-pointer hover:bg-muted/30" onClick={() => navigate(domainPath(d.domain))}>
                        <td className="px-3 py-2">
                          <div className="max-w-[220px] truncate font-medium text-foreground" title={d.domain}>{d.domain}</div>
                          {!d.active && <div className="text-[11px] text-muted-foreground">no reports in the last 7 days</div>}
                        </td>
                        <td className="max-w-[140px] truncate px-3 py-2 text-xs" onClick={(e) => e.stopPropagation()}>
                          {d.company.id != null ? <Link to={companyPath(d.company.id)} className="text-foreground hover:underline">{d.company.name}</Link> : <span className="italic text-muted-foreground">unmapped</span>}
                        </td>
                        <td className="px-3 py-2"><Journey stage={d.stage} />{d.inherited && <span className="ml-1 text-[11px] text-muted-foreground">(inherited)</span>}</td>
                        <td className="px-3 py-2 text-right text-xs tabular-nums text-muted-foreground">{d.volume7d.toLocaleString()}</td>
                        <td className="px-3 py-2"><Pct value={d.compliancePct} /></td>
                        <td className="px-3 py-2 text-xs">
                          {d.readyToEnforce ? <span className="whitespace-nowrap rounded-full bg-warning-soft px-2 py-0.5 font-semibold text-warning">Ready to tighten</span>
                            : <span className="text-muted-foreground">{d.recommendation ? RECOMMENDATION_LABEL[d.recommendation] ?? d.recommendation : '—'}</span>}
                        </td>
                        <td className="px-3 py-2 text-xs tabular-nums text-muted-foreground">{d.daysAtPolicy != null ? `${d.daysAtPolicy}d` : '—'}</td>
                        <td className="px-3 py-2"><Ok ok={d.spf.ok} label={d.spf.label} /></td>
                        <td className="px-3 py-2"><Ok ok={d.dkim.ok} label={d.dkim.label} /></td>
                        <td className="whitespace-nowrap px-3 py-2 text-xs">{d.senders}{d.failingSenders > 0 && <span className="ml-1 font-medium text-destructive">· {d.failingSenders} failing</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="mt-2 text-[11px] text-muted-foreground">
              "Ready to tighten" = dmarcian recommends the next policy step, at least {data.readyPct}% of the last 7 days' mail passed DMARC, and the domain has
              been at its current policy {data.readyDays}+ days. Thresholds: <Link to={ROUTES.admin.domains} className="hover:underline">Admin → Domains</Link>.
            </p>
          </>
        ) : null}
      </div>
    </DesktopOnly>
  );
}

function UnmonitoredTable({ rows }: { rows: UnmonitoredDomain[] }) {
  return (
    <div className="mt-3 overflow-x-auto rounded-lg border border-border bg-card shadow-sm">
      <div className="border-b border-divider px-4 py-2 text-xs text-muted-foreground">
        Custom domains with Microsoft 365 mail in a mapped CIPP tenant that dmarcian doesn't monitor — no visibility of who sends as them.
      </div>
      <table className="w-full text-sm">
        <thead className="whitespace-nowrap bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
          <tr><th className="px-3 py-2 font-medium">Domain</th><th className="px-3 py-2 font-medium">Client</th><th className="px-3 py-2 font-medium">DMARC (DNS)</th><th className="px-3 py-2 text-right font-medium">CIPP domain score</th></tr>
        </thead>
        <tbody className="divide-y divide-divider">
          {rows.length === 0 && <tr><td colSpan={4} className="px-3 py-10 text-center text-muted-foreground">Every client mail domain is monitored.</td></tr>}
          {rows.map((d) => (
            <tr key={d.domain} className="hover:bg-muted/30">
              <td className="px-3 py-2 font-medium text-foreground">{d.domain}</td>
              <td className="px-3 py-2 text-xs">{d.companyId != null ? <Link to={companyPath(d.companyId)} className="text-foreground hover:underline">{d.companyName}</Link> : '—'}</td>
              <td className={cn('px-3 py-2 text-xs', d.dmarc === 'missing' ? 'text-destructive' : d.dmarc === 'none' ? 'text-warning' : 'text-foreground')}>{d.dmarc === 'missing' ? 'No record' : `p=${d.dmarc}`}</td>
              <td className="px-3 py-2 text-right text-xs tabular-nums text-muted-foreground">{d.score != null ? `${d.score}%` : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
